import { UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type VariableItem = { label: string; token: string; hint?: string };
type VariableGroup = { group: string; items: VariableItem[] };

const VARIABLE_GROUPS: VariableGroup[] = [
  {
    group: "Contato",
    items: [
      { label: "Nome do contato", token: "{{nome}}" },
      { label: "Primeiro nome", token: "{{primeiro_nome}}" },
      { label: "Email", token: "{{email}}" },
      { label: "Telefone", token: "{{telefone}}" },
    ],
  },
  {
    group: "Empresa",
    items: [
      { label: "Empresa", token: "{{empresa}}" },
      { label: "Cargo", token: "{{cargo}}" },
      { label: "Setor", token: "{{setor}}" },
      { label: "Cidade", token: "{{cidade}}" },
      { label: "Estado", token: "{{estado}}" },
    ],
  },
  {
    group: "Sistema",
    items: [
      { label: "Data atual", token: "{{data}}" },
      { label: "Hora atual", token: "{{hora}}" },
      { label: "Link de descadastro", token: "{{unsubscribe_url}}" },
    ],
  },
];

interface VariablePickerProps {
  onInsert: (token: string) => void;
  align?: "start" | "end" | "center";
  size?: "sm" | "default" | "icon";
  variant?: "outline" | "ghost" | "secondary";
  buttonClassName?: string;
  iconOnly?: boolean;
  title?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function VariablePicker({
  onInsert,
  align = "start",
  size = "sm",
  variant = "ghost",
  buttonClassName,
  iconOnly = false,
  title = "Inserir variável",
  open,
  onOpenChange,
}: VariablePickerProps) {
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant={variant}
          size={size}
          className={buttonClassName}
          title={title}
        >
          <UserRound className="h-4 w-4" />
          {!iconOnly && <span className="ml-1.5">Variáveis</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-64">
        {VARIABLE_GROUPS.map((group, gi) => (
          <div key={group.group}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {group.group}
            </DropdownMenuLabel>
            {group.items.map((item) => (
              <DropdownMenuItem
                key={item.token}
                onSelect={(e) => {
                  e.preventDefault();
                  onInsert(item.token);
                }}
                className="flex items-center justify-between gap-3"
              >
                <span>{item.label}</span>
                <code className="text-[11px] text-muted-foreground">
                  {item.token}
                </code>
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Helper para inserir um token na posição do caret de um <input> ou <textarea>.
 * Atualiza o estado via `setValue(novoTexto)` e restaura o caret após `start + token.length`.
 */
export function insertAtCursor(
  el: HTMLInputElement | HTMLTextAreaElement | null | undefined,
  current: string,
  token: string,
  setValue: (next: string) => void,
) {
  if (!el) {
    setValue(current + token);
    return;
  }
  const start = el.selectionStart ?? current.length;
  const end = el.selectionEnd ?? current.length;
  const next = current.slice(0, start) + token + current.slice(end);
  setValue(next);
  requestAnimationFrame(() => {
    el.focus();
    const pos = start + token.length;
    try {
      el.setSelectionRange(pos, pos);
    } catch {
      // alguns inputs não suportam setSelectionRange (ex: type=number)
    }
  });
}
